import { isNil, renderLiquid, validateWebhookUrl } from "lopermut"
import { z } from "zod"
import { CapabilityIdentifier } from "../capabilities/dtos"
import {
  AmphoraCustomerTriggerSchema,
  PublicCustomerTriggerJsonSchemaSchema,
  PublicCustomerTriggerSchema,
  TagNameSchema,
} from "../customers/dtos"
import { Errors } from "../errors"
import { CreateGoalSchema } from "../goals/dtos"
import { AmphoraSideEnum } from "../integrations/amphora/dtos"
import { CreateFrontAppTicketConfigSchema } from "../integrations/frontapp/response_data"
import { CreateGorgiasTicketConfigSchema } from "../integrations/gorgias/response_data"
import { CreateZendeskTicketConfigSchema } from "../integrations/zendesk/response_data"
import { AttachmentSchema } from "../messages/attachments"
import { createFilterBySchema, oneOrMany } from "../shared/filter_dtos"
import { PaginationSchema } from "../shared/pagination_dtos"
import { QuerySchema } from "../shared/query_dtos"
import { createSortSchema } from "../shared/sort_dtos"
import {
  BigBlueCampaignTriggerEnum,
  BigBlueBackorderConfigSchema,
  CampaignLaunchAudienceSchema,
  CampaignLogKindEnum,
  CampaignStatusEnum,
  CampaignTriggerIntegrationSchema,
  CampaignTriggerTypeEnum,
  ChannelEnum,
  EditableCampaignTriggerTypeEnum,
  EmailCampaignChannelIntegrationSchema,
  FlowEnum,
  InstagramVerifiedOptions,
  OpeningEmailSchema,
  ShopifyCampaignTriggerEnum,
  getGmailIntegrationIdsFromTriggerIntegration,
  getOutlookIntegrationIdsFromTriggerIntegration,
} from "./commonTypes"
import { validateWorkflowCondition } from "./conditions"
import {
  ActionTypeEnum,
  CampaignCallbackHeadersSchema,
  WorkflowConditionFieldEnum,
  WorkflowConditionLogicOperatorEnum,
  WorkflowConditionOperatorEnum,
  WorkflowEventEnum,
  WorkflowNodeTypeEnum,
} from "./response_data"

const sortFields = ["name", "status", "type", "createdAt"] as const
export const CampaignsSortSchema = createSortSchema(sortFields, "createdAt", "desc")

const WorkflowEventSchemaEnum = { ...WorkflowEventEnum } as const
const WorkflowConditionFieldSchemaEnum = { ...WorkflowConditionFieldEnum } as const

export const CampaignsFilterBySchema = createFilterBySchema([
  {
    key: "status",
    value: oneOrMany(z.enum(CampaignStatusEnum)),
  } as const,
  {
    key: "ids",
    value: z.array(z.string().transform(BigInt)).optional(),
  },
  {
    key: "archived",
    value: z
      .union([z.literal("true"), z.literal("false"), z.boolean()])
      .transform((val) => (typeof val === "boolean" ? val : val === "true"))
      .optional(),
  },
  {
    key: "flow",
    value: oneOrMany(z.enum(FlowEnum)),
  } as const,
] as const)

export const GetCampaignsSchema = z.object({
  ...PaginationSchema.extend(QuerySchema.shape)
    .extend(CampaignsSortSchema.shape)
    .extend(CampaignsFilterBySchema.shape).shape,
})

export const GetCampaignSchema = z.object({
  id: z.string().transform(BigInt),
})

export const GetCampaignsStatsSchema = z.object({
  campaignId: z.string().transform(BigInt).optional(),
})

export const GetCampaignsImpactSchema = z.object({
  campaignId: z.string().transform(BigInt).optional(),
})

export const GetCampaignReachSchema = z
  .object({
    customerIds: z
      .array(z.union([z.string(), z.number(), z.bigint()]))
      .transform((ids) => ids.map(BigInt))
      .optional(),
    audienceSource: CampaignLaunchAudienceSchema.optional(),
    closeOngoingConversations: z.boolean().optional(),
  })
  .optional()

export const CampaignLogsFilterBySchema = createFilterBySchema([
  {
    key: "kind",
    value: z.enum(CampaignLogKindEnum).optional(),
  } as const,
])

export const CampaignLogsParamsSchema = z.object({
  id: z.string().transform(BigInt),
})

export const CampaignLogsQuerySchema = z.object({
  ...PaginationSchema.extend(CampaignLogsFilterBySchema.shape).shape,
})

export const UpdateCampaignTemplateStatusSchema = z.object({
  status: z.enum(["active", "inactive"]),
})

export const AddTemplatesToCampaignSchema = z.object({
  templateIds: z.array(z.string()),
})

export const WorkflowEventConversationGoalAchievedConfigSchema = z.object({
  goal: z.union([z.bigint(), z.string()]).transform(BigInt),
})

export const WorkflowEventCapabilityConfigSchema = z.object({
  capabilityId: z.union([z.bigint(), z.string()]).transform(BigInt),
})

export const CampaignSingleConditionRuleSchema = z.object({
  field: z.enum(WorkflowConditionFieldSchemaEnum),
  operator: z.enum(WorkflowConditionOperatorEnum),
  value: z.any().nullish(),
  logic: z.enum(WorkflowConditionLogicOperatorEnum).nullish(),
  config: z.record(z.string(), z.any()).nullish(),
})

export const WorkflowConditionLLMPromptConfigSchema = z.object({
  prompt: z.string().trim().min(1),
})

export const CAMPAIGN_ACTION_WEBHOOK_DEFAULT_PAYLOAD = "{{ data | json }}"
export const CAMPAIGN_ACTION_IN_CONVERSATION_WHATSAPP_DEFAULT_PAYLOAD =
  "Hey {{ data.customer.name}}!"

export const WorkflowActionWebhookConfigSchema = z.object({
  url: z
    .url({
      message: Errors.INVALID_URL,
    })
    .refine(
      (url) => {
        try {
          validateWebhookUrl(url)
          return true
        } catch {
          return false
        }
      },
      { message: Errors.INVALID_URL },
    ),
  headers: z.array(
    z.object(
      {
        key: z.string(),
        value: z.string(),
      },
      {
        message: Errors.INVALID_OBJECT,
      },
    ),
  ),
  payload: z
    .string()
    .transform((val) => {
      if (isNil(val)) return null
      if (val === "") return null
      return val
    })
    .refine(
      (val) => {
        if (isNil(val)) return true
        if (typeof val !== "string") return false

        try {
          JSON.parse(renderLiquid(val, {}))
          return true
        } catch (error) {
          return false
        }
      },
      {
        message: Errors.INVALID_JSON,
      },
    )
    .default(CAMPAIGN_ACTION_WEBHOOK_DEFAULT_PAYLOAD),
})

export const WorkflowActionInternalEmailConfigSchema = z.object({
  email: z.object({
    subject: z.string(),
    recipients: z.array(z.object({ value: z.string().transform(BigInt), label: z.string() })),
    body: z.string(),
  }),
})

export const TestInternalEmailSchema = z.object({
  data: WorkflowActionInternalEmailConfigSchema,
  eventPayload: z.record(z.string(), z.any()),
})

export const WorkflowActionInConversationMessageConfigSchema = z.object({
  message: z.string(),
  locale: z.string().trim().min(1).optional(),
})

export const WorkflowActionDisableAgentConfigSchema = z.object({
  hours: z.coerce.number().finite().positive(),
})

export const UpdateCustomerTagsOperationEnum = {
  Add: "Add",
  Remove: "Remove",
} as const

export const WorkflowActionUpdateCustomerTagsConfigSchema = z.object({
  operation: z.enum(UpdateCustomerTagsOperationEnum),
  tags: z.array(TagNameSchema).min(1),
})

// Allowlisted capability identifiers that can be triggered via the TriggerCapability workflow action
export const TriggerCapableCapabilityIdentifier = {
  RequestReview: CapabilityIdentifier.RequestReview,
} as const

export const TriggerCapabilityActionConfigSchema = z.object({
  capabilityIdentifier: z.enum(TriggerCapableCapabilityIdentifier),
})

export function isEventConfigValid(event: keyof typeof WorkflowEventEnum, config: unknown) {
  switch (event) {
    case WorkflowEventEnum.ConversationGoalAchieved:
      return WorkflowEventConversationGoalAchievedConfigSchema.safeParse(config).success
    case WorkflowEventEnum.CapabilityEvaluated:
    case WorkflowEventEnum.CapabilityExecuted:
      return WorkflowEventCapabilityConfigSchema.safeParse(config).success
    default:
      return true
  }
}

export function isActionConfigValid(action: keyof typeof ActionTypeEnum, config: unknown) {
  switch (action) {
    case ActionTypeEnum.CreateZendeskTicket:
      return CreateZendeskTicketConfigSchema.safeParse(config).success
    case ActionTypeEnum.CreateGorgiasTicket:
      return CreateGorgiasTicketConfigSchema.safeParse(config).success
    case ActionTypeEnum.CreateFrontAppTicket:
      return CreateFrontAppTicketConfigSchema.safeParse(config).success
    case ActionTypeEnum.SendWebhook:
      return WorkflowActionWebhookConfigSchema.safeParse(config).success
    case ActionTypeEnum.SendInternalEmail:
      return WorkflowActionInternalEmailConfigSchema.safeParse(config).success
    case ActionTypeEnum.SendInConversationMessage:
      return WorkflowActionInConversationMessageConfigSchema.safeParse(config).success
    case ActionTypeEnum.TriggerCapability:
      return TriggerCapabilityActionConfigSchema.safeParse(config).success
    case ActionTypeEnum.DisableAgent:
      return WorkflowActionDisableAgentConfigSchema.safeParse(config).success
    case ActionTypeEnum.UpdateCustomerTags:
      return WorkflowActionUpdateCustomerTagsConfigSchema.safeParse(config).success
  }
}

export const WorkflowNodeSchema = z
  .object({
    id: z.union([z.string(), z.bigint()]).transform((id) => id.toString()), // uuid in case it's still not persisted
    parentId: z
      .union([z.string(), z.number(), z.bigint()])
      .transform((id) => id.toString())
      .nullish()
      .nullable(), // uuid in case it's still not persisted
    order: z.number().nullish(),
    branch: z.enum(["Pass", "Fail"]).optional(),
  })
  .and(
    z
      .discriminatedUnion("type", [
        z.object({
          type: z.literal(WorkflowNodeTypeEnum.Event),
          event: z.enum(WorkflowEventSchemaEnum),
          config: z.record(z.string(), z.any()).nullish(),
        }),
        z.object({
          type: z.literal(WorkflowNodeTypeEnum.Action),
          action: z.enum(ActionTypeEnum),
          config: z.record(z.string(), z.any()).nullish(),
        }),
        z.object({
          type: z.literal(WorkflowNodeTypeEnum.Condition),
          conditions: z.array(CampaignSingleConditionRuleSchema),
        }),
      ])
      .refine(
        (val) => {
          if (val.type !== WorkflowNodeTypeEnum.Condition) return true

          for (const condition of val.conditions) {
            if (!validateWorkflowCondition(condition)) return false
          }

          return true
        },
        {
          path: ["conditions"],
          message: Errors.CAMPAIGN_CONDITION_INVALID,
        },
      )
      .refine(
        (val) => {
          if (val.type !== WorkflowNodeTypeEnum.Action) return true
          return isActionConfigValid(val.action, val.config)
        },
        {
          path: ["action"],
          message: Errors.CAMPAIGN_ACTION_INVALID,
        },
      )
      .refine(
        (val) => {
          if (val.type !== WorkflowNodeTypeEnum.Event) return true
          return isEventConfigValid(val.event, val.config)
        },
        {
          path: ["event"],
          message: Errors.CAMPAIGN_EVENT_INVALID,
        },
      ),
  )

// Shared validation: every Event node must have at least one Action in its subtree
export const validateEventHasAction = (payload: {
  nodes?: { id: string; parentId?: string | null; type: string }[] | null | undefined
}) => {
  const nodes = payload.nodes
  if (!nodes || nodes.length === 0) return true

  const childrenMap = new Map<string, typeof nodes>()
  for (const node of nodes) {
    if (node.parentId != null) {
      const siblings = childrenMap.get(node.parentId) ?? []
      siblings.push(node)
      childrenMap.set(node.parentId, siblings)
    }
  }

  const hasActionInSubtree = (nodeId: string, visited = new Set<string>()): boolean => {
    if (visited.has(nodeId)) return false
    visited.add(nodeId)
    const children = childrenMap.get(nodeId) ?? []
    for (const child of children) {
      if (child.type === "Action") return true
      if (hasActionInSubtree(child.id, visited)) return true
    }
    return false
  }

  for (const node of nodes) {
    if (node.type === "Event" && !hasActionInSubtree(node.id)) {
      return false
    }
  }

  return true
}

/**
 * Default Liquid template for the API channel callback body. Reproduces the legacy fixed body so
 * existing campaigns keep working. Dynamic values use the `json` filter so the rendered output is
 * valid JSON even when the value contains quotes/newlines/emojis.
 */
export const CAMPAIGN_API_CALLBACK_DEFAULT_PAYLOAD = `{
  "referenceId": {{ referenceId | json }},
  "message": {{ message | json }},
  "createdAt": {{ createdAt | json }}
}`

/** Example data shown in the payload editor (available variables + live preview). */
export const CAMPAIGN_API_CALLBACK_SAMPLE_DATA = {
  referenceId: "ref_123",
  message: "Hi! How can I help you today?",
  createdAt: "2026-06-30T12:00:00.000Z",
  conversation: { externalId: "conv_abc", source: "amphora" },
  company: { id: "1", name: "Acme" },
  companyClient: { externalId: "client_789", name: "Acme Logistics" },
}

/**
 * Liquid JSON template for the API channel callback body. Empty becomes null (use the default); a
 * non-empty value must render to valid JSON against an empty context.
 */
export const CampaignCallbackPayloadSchema = z
  .string()
  .transform((val) => (isNil(val) || val.trim() === "" ? null : val))
  .refine(
    (val) => {
      if (isNil(val)) return true
      try {
        JSON.parse(renderLiquid(val, {}))
        return true
      } catch {
        return false
      }
    },
    { message: Errors.INVALID_JSON },
  )

export const UpdateCampaignBaseSchema = z.object({
  name: z.string().nullish(),
  description: z.string().nullish(),
  toneOfVoice: z.string().nullish(),
  goals: z
    .array(
      z.union([
        CreateGoalSchema,
        z.object({
          id: z.string(),
          shotsInCampaign: z.number().nullish(),
          isUnderlying: z.boolean().nullish(),
        }),
      ]),
    )
    .nullish(),
  channel: z.enum(ChannelEnum).nullish(),
  defaultLanguage: z.string().nullish(),
  isLogistics: z.boolean().nullish(),
  whatsappNumberId: z.string().nullish(),
  triggerIntegration: CampaignTriggerIntegrationSchema.nullish(),
  templates: z.array(z.string()).nullish(),
  openingMessages: z.array(z.string()).nullish(),
  callbackUrl: z.string().nullish(),
  callbackHeaders: CampaignCallbackHeadersSchema.nullish(),
  callbackPayload: CampaignCallbackPayloadSchema.nullish(),
  flow: z.enum(FlowEnum).nullish(),
  trigger: z.enum(EditableCampaignTriggerTypeEnum).nullish(),
  nodes: z.array(WorkflowNodeSchema).nullish(),
})

export const UpdateCampaignSchema = UpdateCampaignBaseSchema.refine(validateEventHasAction, {
  path: ["nodes"],
  message: Errors.CAMPAIGN_EVENT_WITHOUT_ACTION,
})

export const InstagramCampaignTriggerMetadataSchema = z.object({
  delayInDays: z.number().int().positive().nullish().default(3),
  discardPhrases: z.array(z.string()).nullish().default([]),
  audience: z
    .object({
      verified: InstagramVerifiedOptions.nullish(),
      minFollowersCount: z.number().nullish(),
      maxFollowersCount: z.number().nullish(),
    })
    .default({}),
})

export const MessengerCampaignTriggerMetadataSchema = z.object({
  delayInDays: z.number().int().positive().nullish().default(3),
  discardPhrases: z.array(z.string()).nullish().default([]),
})

export const EmailCampaignTriggerMetadataSchema = z.object({
  delayInDays: z.number().int().positive().nullish().default(3),
  openingEmail: OpeningEmailSchema.nullish(),
  openingEmailLocaleOrder: z.array(z.string()).nullish(),
})

export const ShopifyCampaignTriggerMetadataSchema = z.object({
  trigger: z.enum(ShopifyCampaignTriggerEnum),
  delayInDays: z.number().int().nonnegative().nullish(),
  email: EmailCampaignChannelIntegrationSchema.nullish(),
})

export const BigBlueCampaignTriggerMetadataSchema = z.object({
  trigger: z.enum(BigBlueCampaignTriggerEnum),
  backorder: BigBlueBackorderConfigSchema.optional(),
  email: EmailCampaignChannelIntegrationSchema.nullish(),
})

export const InstagramCampaignTriggerCommentMetadataSchema = z.object({
  replies: z.array(z.string()).nullish(),
  allowList: z.array(z.string()).nullish(),
})

export const LaunchCampaignSchema = UpdateCampaignBaseSchema.pick({
  callbackUrl: true,
  callbackHeaders: true,
  flow: true,
})
  .extend({
    defaultLanguage: z
      .string({ error: Errors.CAMPAIGN_DEFAULT_LANGUAGE_REQUIRED })
      .nullish()
      .nullable(),
    goals: z.array(
      z.union([
        CreateGoalSchema,
        z.object({
          id: z.string(),
          shotsInCampaign: z.number().nullish(),
          isUnderlying: z.boolean().nullish(),
        }),
      ]),
    ),
    channel: z.enum(ChannelEnum, {
      error: Errors.CAMPAIGN_CHANNEL_REQUIRED,
    }),
    whatsappNumberId: z.string().nullish(),
    triggerIntegration: CampaignTriggerIntegrationSchema.nullish(),
    templates: z.array(z.union([z.object({ language: z.string(), name: z.string() }), z.string()])),
    openingMessages: z.array(
      z.union([z.object({ locale: z.string(), identifier: z.string() }), z.string()]),
    ),
    trigger: z.enum(EditableCampaignTriggerTypeEnum, {
      error: Errors.CAMPAIGN_TRIGGER_INVALID,
    }),
    nodes: z.array(WorkflowNodeSchema),
    flow: z.enum(FlowEnum, {
      error: Errors.CAMPAIGN_FLOW_REQUIRED,
    }),
  })
  .refine(
    (payload) => {
      const requiresWhatsappNumber =
        (payload.trigger === CampaignTriggerTypeEnum.Webhook ||
          payload.trigger === CampaignTriggerTypeEnum.Manual ||
          payload.trigger === CampaignTriggerTypeEnum.Shopify ||
          payload.trigger === CampaignTriggerTypeEnum.BigBlue) &&
        payload.channel === ChannelEnum.Whatsapp

      if (requiresWhatsappNumber && !payload.whatsappNumberId) {
        return false
      }

      return true
    },
    { message: Errors.CAMPAIGN_NUMBER_OR_INTEGRATION_ID_REQUIRED },
  )
  .superRefine((data, ctx) => {
    if (data.channel !== ChannelEnum.Api) return
    const url = data.callbackUrl
    if (!url) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: Errors.CAMPAIGN_CALLBACK_URL_REQUIRED,
        path: ["callbackUrl"],
      })
      return
    }
  })
  .refine(
    (payload) => {
      if (payload.trigger === CampaignTriggerTypeEnum.WhatsappDefault) return true

      const requiresTemplates =
        (payload.trigger === CampaignTriggerTypeEnum.Webhook ||
          payload.trigger === CampaignTriggerTypeEnum.Manual ||
          payload.trigger === CampaignTriggerTypeEnum.Shopify ||
          payload.trigger === CampaignTriggerTypeEnum.BigBlue) &&
        payload.channel === ChannelEnum.Whatsapp

      return !requiresTemplates || payload.templates.length >= 1
    },
    { message: Errors.CAMPAIGN_TEMPLATES_REQUIRED },
  )
  .refine(
    (payload) =>
      payload.channel !== ChannelEnum.Whatsapp ||
      payload.flow !== FlowEnum.Outbound ||
      !!payload.defaultLanguage,
    { message: Errors.CAMPAIGN_DEFAULT_LANGUAGE_REQUIRED, path: ["defaultLanguage"] },
  )
  .refine(
    (payload) => {
      const requiresOpeningMessages =
        payload.channel === ChannelEnum.Whatsapp &&
        (payload.trigger === CampaignTriggerTypeEnum.Widget ||
          payload.trigger === CampaignTriggerTypeEnum.QR ||
          payload.trigger === CampaignTriggerTypeEnum.Link)

      return !requiresOpeningMessages || payload.openingMessages.length >= 1
    },
    { message: Errors.CAMPAIGN_OPENING_MESSAGES_REQUIRED },
  )
  .refine(
    (payload) => {
      // requires to have a QR value
      if (payload.trigger === CampaignTriggerTypeEnum.QR) {
        const integration = payload.triggerIntegration
        if (integration?.type !== "qr" || !integration.qrId) {
          return false
        }
      }

      return true
    },
    { message: Errors.CAMPAIGN_TRIGGER_QR_WITHOUT_QR },
  )
  .refine(
    (payload) => {
      // requires to have an Instagram account
      const isInstagramTrigger =
        payload.trigger === CampaignTriggerTypeEnum.InstagramDirectMessage ||
        payload.trigger === CampaignTriggerTypeEnum.InstagramCommentReply
      if (isInstagramTrigger) {
        const integration = payload.triggerIntegration
        const hasAccount =
          (integration?.type === "instagramDirectMessage" ||
            integration?.type === "instagramCommentReply") &&
          !!integration.instagramIntegrationId
        if (!hasAccount) {
          return false
        }
      }

      return true
    },
    { message: Errors.CAMPAIGN_TRIGGER_INSTAGRAM_WITHOUT_ACCOUNT },
  )
  .refine(
    (payload) => {
      const isMessengerTrigger =
        payload.trigger === CampaignTriggerTypeEnum.MessengerDirectMessage ||
        payload.trigger === CampaignTriggerTypeEnum.MessengerCommentReply
      if (!isMessengerTrigger) return true

      const integration = payload.triggerIntegration

      return (
        (integration?.type === "messengerDirectMessage" ||
          integration?.type === "messengerCommentReply") &&
        !!integration.messengerIntegrationId
      )
    },
    { message: Errors.CAMPAIGN_TRIGGER_MESSENGER_WITHOUT_PAGE },
  )
  .refine(
    (payload) => {
      // check instagram audience values — only the DM trigger filters by audience
      if (
        payload.trigger === CampaignTriggerTypeEnum.InstagramDirectMessage &&
        payload.triggerIntegration?.type === "instagramDirectMessage"
      ) {
        const { minFollowersCount, maxFollowersCount } = payload.triggerIntegration.audience ?? {}
        if (
          !!minFollowersCount &&
          !!maxFollowersCount &&
          (minFollowersCount > maxFollowersCount || maxFollowersCount < minFollowersCount)
        ) {
          return false
        }
      }

      return true
    },
    { message: Errors.CAMPAIGN_TRIGGER_INSTAGRAM_INVALID_AUDIENCE },
  )
  .refine(
    (payload) => {
      if (payload.trigger !== CampaignTriggerTypeEnum.Shopify) return true
      return payload.triggerIntegration?.type === "shopify"
    },
    { message: Errors.CAMPAIGN_TRIGGER_INVALID, path: ["triggerIntegration"] },
  )
  .refine(
    (payload) => {
      if (payload.trigger !== CampaignTriggerTypeEnum.BigBlue) return true
      return payload.triggerIntegration?.type === "bigblue"
    },
    { message: Errors.CAMPAIGN_TRIGGER_INVALID, path: ["triggerIntegration"] },
  )
  .refine(validateEventHasAction, {
    path: ["nodes"],
    message: Errors.CAMPAIGN_EVENT_WITHOUT_ACTION,
  })
  .superRefine((data, ctx) => {
    if (
      (data.trigger === CampaignTriggerTypeEnum.Webhook ||
        data.trigger === CampaignTriggerTypeEnum.Manual ||
        data.trigger === CampaignTriggerTypeEnum.Shopify ||
        data.trigger === CampaignTriggerTypeEnum.BigBlue) &&
      data.flow == null
    ) {
      ctx.addIssue({
        code: "custom",
        message: Errors.CAMPAIGN_TRIGGER_INVALID,
        path: ["flow"],
      })
    }

    if (data.channel === ChannelEnum.Api && !data.callbackUrl) {
      ctx.addIssue({
        code: "custom",
        message: Errors.CAMPAIGN_CALLBACK_URL_REQUIRED,
        path: ["callbackUrl"],
      })
      return
    }

    const isOutboundEmail =
      data.flow === FlowEnum.Outbound &&
      (data.channel === ChannelEnum.Gmail || data.channel === ChannelEnum.Outlook)
    const emailIntegration =
      data.triggerIntegration?.type === "shopify" || data.triggerIntegration?.type === "bigblue"
        ? data.triggerIntegration.email
        : data.triggerIntegration
    const openingEmail =
      emailIntegration?.type === "gmail" || emailIntegration?.type === "outlook"
        ? emailIntegration.openingEmail
        : null
    const defaultOpeningEmail = data.defaultLanguage ? openingEmail?.[data.defaultLanguage] : null

    if (
      isOutboundEmail &&
      data.channel === ChannelEnum.Gmail &&
      emailIntegration?.type !== "gmail"
    ) {
      ctx.addIssue({
        code: "custom",
        message: Errors.CAMPAIGN_CHANNEL_GMAIL_WITHOUT_ACCOUNT,
        path: ["triggerIntegration"],
      })
    }
    if (
      isOutboundEmail &&
      data.channel === ChannelEnum.Outlook &&
      emailIntegration?.type !== "outlook"
    ) {
      ctx.addIssue({
        code: "custom",
        message: Errors.CAMPAIGN_CHANNEL_OUTLOOK_WITHOUT_ACCOUNT,
        path: ["triggerIntegration"],
      })
    }

    if (
      isOutboundEmail &&
      (!data.defaultLanguage ||
        !Object.keys(openingEmail ?? {}).length ||
        !defaultOpeningEmail?.subject?.trim() ||
        !defaultOpeningEmail.body?.trim() ||
        Object.values(openingEmail ?? {}).some(
          (email) => !email.subject?.trim() || !email.body?.trim(),
        ))
    ) {
      ctx.addIssue({
        code: "custom",
        message: Errors.CAMPAIGN_TRIGGER_INVALID,
        path: ["triggerIntegration", "openingEmail"],
      })
    }
    if (
      isOutboundEmail &&
      emailIntegration?.type === "gmail" &&
      getGmailIntegrationIdsFromTriggerIntegration(data.triggerIntegration).length === 0
    ) {
      ctx.addIssue({
        code: "custom",
        message: Errors.CAMPAIGN_CHANNEL_GMAIL_WITHOUT_ACCOUNT,
        path: ["triggerIntegration"],
      })
    }
    if (
      isOutboundEmail &&
      emailIntegration?.type === "outlook" &&
      getOutlookIntegrationIdsFromTriggerIntegration(data.triggerIntegration).length === 0
    ) {
      ctx.addIssue({
        code: "custom",
        message: Errors.CAMPAIGN_CHANNEL_OUTLOOK_WITHOUT_ACCOUNT,
        path: ["triggerIntegration"],
      })
    }

    if (data.trigger === CampaignTriggerTypeEnum.EmailInbox) {
      if (data.channel === ChannelEnum.Outlook) {
        if (
          data.triggerIntegration?.type !== "outlook" ||
          getOutlookIntegrationIdsFromTriggerIntegration(data.triggerIntegration).length === 0
        ) {
          ctx.addIssue({
            code: "custom",
            message: Errors.CAMPAIGN_CHANNEL_OUTLOOK_WITHOUT_ACCOUNT,
            path: ["triggerIntegration"],
          })
        }
        return
      }

      if (
        data.triggerIntegration?.type !== "gmail" ||
        getGmailIntegrationIdsFromTriggerIntegration(data.triggerIntegration).length === 0
      ) {
        ctx.addIssue({
          code: "custom",
          message: Errors.CAMPAIGN_CHANNEL_GMAIL_WITHOUT_ACCOUNT,
          path: ["triggerIntegration"],
        })
      }
    }
  })

export const UpdateCampaignStatusSchema = z.object({
  status: z.enum(CampaignStatusEnum),
  campaignPayload: LaunchCampaignSchema.optional(),
})

const TriggerActionSchema = z.object({
  type: z.literal("trustpilot"),
  link: z.string(),
})

export const EventIntegrationsDataSchema = z.object({
  shopify: z.object({ productIds: z.array(z.string()).nullish() }).nullish(),
})

const BaseTriggerSchema = z.object({
  event: z.record(z.string(), z.any()).optional(),
  actions: z.array(TriggerActionSchema).optional(),
  integrations: EventIntegrationsDataSchema.optional(),
  closeOngoingConversations: z.boolean().optional(),
  delayInHours: z.number().optional(),
})

const KnownTriggerSchema = BaseTriggerSchema.extend({
  customerId: z
    .union([z.string(), z.number(), z.bigint()])
    .transform((val) => (typeof val === "bigint" ? val : BigInt(String(val)))),
})

const OutboundTriggerSchema = BaseTriggerSchema.extend({ customer: PublicCustomerTriggerSchema })
const InboundTriggerSchema = BaseTriggerSchema.extend({
  referenceId: z.string(),
  message: z.string(),
  attached_files: z.array(AttachmentSchema).default([]),
  customer: PublicCustomerTriggerSchema,
  agent: z.string().nullish(),
  orderId: z.string().nullish(),
})

export const AmphoraProcessTriggerSchema = InboundTriggerSchema.extend({
  customer: AmphoraCustomerTriggerSchema,
  orderId: z.string().trim().min(1),
  side: z.enum(AmphoraSideEnum).nullish(),
  postedVia: z.string().nullish(),
  source: z.string().nullish(),
})

// Order matters: these are non-discriminated unions and Zod strips unknown keys, so an inbound
// payload (which also carries a valid `customer`) would otherwise match `OutboundTriggerSchema`
// first and silently drop `referenceId`/`message`. The more specific inbound schema must come
// before the outbound one.
export const PublicProcessTriggerSchema = z.union([InboundTriggerSchema, OutboundTriggerSchema])

export const ProcessTriggerSchema = z.union([
  AmphoraProcessTriggerSchema,
  InboundTriggerSchema,
  KnownTriggerSchema,
  OutboundTriggerSchema,
])

export const BulkProcessRawTriggerSchema = z.object({
  triggers: z.array(OutboundTriggerSchema),
})

export const BulkProcessKnownTriggerSchema = z.object({
  triggers: z.array(KnownTriggerSchema),
  importId: z.string().uuid().optional(),
  closeOngoingConversations: z.boolean().optional(),
})

export const PublicBulkProcessTriggerSchema = z.object({
  triggers: z.array(PublicProcessTriggerSchema),
})

const CampaignLaunchScheduledAtSchema = z.iso
  .datetime()
  .transform((value) => new Date(value))
  .refine((date) => date > new Date(), { message: Errors.CAMPAIGN_SCHEDULE_IN_PAST })

export const GetCampaignLaunchSchema = z.object({
  id: z.string().transform(BigInt),
  launchId: z.string().transform(BigInt),
})

export const GetCampaignLaunchAudienceSchema = z.object({
  audienceSource: CampaignLaunchAudienceSchema,
})

export const CreateCampaignLaunchSchema = z.object({
  audienceSource: CampaignLaunchAudienceSchema,
  closeOngoingConversations: z.boolean().optional(),
  scheduledAt: CampaignLaunchScheduledAtSchema.optional(),
})

export const UpdateCampaignLaunchSchema = z.object({
  scheduledAt: CampaignLaunchScheduledAtSchema,
})

export const GetCampaignsActivitySchema = z.object({
  campaignIds: z.array(z.string()).transform((ids) => ids.map(BigInt)),
})

// JSON Schema-compatible version (without transforms)
export const PublicProcessBulkTriggerJsonSchemaSchema = z.object({
  triggers: z.array(
    z.object({
      customer: PublicCustomerTriggerJsonSchemaSchema,
      closeOngoingConversations: z.boolean().nullish(),
      event: z.record(z.string(), z.any()).nullish(),
      delayInHours: z.number().nullish(),
      actions: z.array(TriggerActionSchema).nullish(),
      batch: z.boolean().nullish(),
    }),
  ),
})
